import {
  type Color,
  FLAG_DROP,
  type MoveEvent,
  type Position,
  WHITE,
  isNeutral,
  makePiece,
  moveFlags,
  moveFrom,
  movePromo,
  moveTo,
  moveToUci,
  pieceColor,
  pieceDef,
  squareName,
} from '@chessbut/engine';
import { type CSSProperties, type KeyboardEvent, type PointerEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { type Decoration, type VariantUi, imageFor, labelFor, pieceImage } from '../variants/ui.ts';

export interface BoardProps {
  position: Position;
  orientation: Color;
  ui: VariantUi;
  /** Moves the user may make right now (empty = read-only). */
  legalMoves: number[];
  /** What happened on the move that produced `position`; animated when `animKey` changes. */
  events: readonly MoveEvent[] | null;
  animKey: number;
  /** The last move was dragged into place, so don't slide it. */
  dragged: boolean;
  lastMove: { from: number; to: number } | null;
  checkSquare: number;
  dangerSquare: number;
  decorations: Decoration[];
  showLegalMoves: boolean;
  showHints: boolean;
  coordinates: boolean;
  onMove(uci: string, dragged: boolean): void;
}

interface DragState {
  sq: number;
  pointerId: number;
  startX: number;
  startY: number;
  active: boolean;
  wasSelected: boolean;
}

interface Anim {
  from: number;
  slow: boolean;
}

const pct = (n: number) => `${n * 12.5}%`;

export function Board(props: BoardProps) {
  const { position, orientation, legalMoves, events, animKey, ui } = props;
  const boardRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const drag = useRef<DragState | null>(null);
  const [promotion, setPromotion] = useState<{ from: number; to: number; dragged: boolean; options: number[] } | null>(null);
  /** Keyboard cursor (arrow keys move it, Enter/Space acts like a tap). */
  const [cursor, setCursor] = useState<number | null>(null);
  const id = useId();

  const movesFrom = useMemo(() => {
    const map = new Map<number, number[]>();
    for (const m of legalMoves) {
      if (moveFlags(m) & FLAG_DROP) continue;
      const list = map.get(moveFrom(m)) ?? [];
      list.push(m);
      map.set(moveFrom(m), list);
    }
    return map;
  }, [legalMoves]);

  /** Placement moves (a goose, a dropped piece) by target square. */
  const dropsTo = useMemo(() => {
    const map = new Map<number, number[]>();
    for (const m of legalMoves) {
      if (!(moveFlags(m) & FLAG_DROP)) continue;
      const list = map.get(moveTo(m)) ?? [];
      list.push(m);
      map.set(moveTo(m), list);
    }
    return map;
  }, [legalMoves]);

  // New position: clear the selection, but if only one piece can move (the goose), pick it up.
  useEffect(() => {
    setSelected(movesFrom.size === 1 && dropsTo.size === 0 ? movesFrom.keys().next().value! : null);
    setPromotion(null);
  }, [position, legalMoves, movesFrom, dropsTo]);

  const targets = useMemo(() => {
    const set = new Set<number>();
    if (selected !== null) for (const m of movesFrom.get(selected) ?? []) set.add(moveTo(m));
    return set;
  }, [selected, movesFrom]);

  // Squares as displayed: x/y from the top-left corner.
  const toXY = (sq: number) => (orientation === WHITE ? { x: sq & 7, y: 7 - (sq >> 3) } : { x: 7 - (sq & 7), y: sq >> 3 });
  const fromXY = (x: number, y: number) => (orientation === WHITE ? (7 - y) * 8 + x : y * 8 + (7 - x));

  const squareAt = (clientX: number, clientY: number): number | null => {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = Math.floor(((clientX - rect.left) / rect.width) * 8);
    const y = Math.floor(((clientY - rect.top) / rect.height) * 8);
    if (x < 0 || x > 7 || y < 0 || y > 7) return null;
    return fromXY(x, y);
  };

  const tryMove = (from: number, to: number, dragged: boolean): boolean => {
    const candidates = (movesFrom.get(from) ?? []).filter((m) => moveTo(m) === to);
    if (!candidates.length) return false;
    const promos = candidates.map(movePromo).filter(Boolean);
    if (promos.length > 1) {
      setPromotion({ from, to, dragged, options: promos });
      return true;
    }
    setSelected(null);
    props.onMove(moveToUci(candidates[0]), dragged);
    return true;
  };

  /** An action done on a piece where it stands (crowning an heir): the only "move" is to itself. */
  const inPlace = (sq: number): number | null => {
    const list = movesFrom.get(sq);
    return list && list.every((m) => moveTo(m) === sq) ? list[0] : null;
  };

  /** A tap (or Enter) on a square without dragging. */
  const activate = (sq: number) => {
    if (promotion) return;
    const act = inPlace(sq);
    if (act !== null) {
      setSelected(null);
      props.onMove(moveToUci(act), false);
      return;
    }
    if (selected !== null && targets.has(sq)) tryMove(selected, sq, false);
    else if (selected === null && dropsTo.has(sq)) props.onMove(moveToUci(dropsTo.get(sq)![0]), false);
    else if (movesFrom.has(sq)) setSelected(selected === sq ? null : sq);
    else setSelected(null);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (promotion) {
      if (e.key === 'Escape') setPromotion(null);
      return;
    }
    const at = cursor ?? selected ?? (orientation === WHITE ? 4 : 60);
    const { x, y } = toXY(at);
    const step: Record<string, [number, number]> = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] };
    if (step[e.key]) {
      e.preventDefault();
      const [dx, dy] = step[e.key];
      setCursor(fromXY(Math.min(7, Math.max(0, x + dx)), Math.min(7, Math.max(0, y + dy))));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setCursor(at);
      activate(at);
    } else if (e.key === 'Escape') setSelected(null);
  };

  const squareLabel = (sq: number): string => {
    const p = position.board[sq];
    const parts = [`${squareName(sq)}, ${p ? labelFor(ui, position, sq, p) : 'empty'}`];
    if (sq === selected) parts.push('selected');
    if (targets.has(sq) || (selected === null && dropsTo.has(sq))) parts.push('you can move here');
    else if (selected === null && movesFrom.has(sq)) parts.push('can move');
    return parts.join(', ');
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || promotion) return;
    const sq = squareAt(e.clientX, e.clientY);
    if (sq === null) return;
    setCursor(null);
    const act = inPlace(sq);
    if (act !== null) {
      setSelected(null);
      props.onMove(moveToUci(act), false);
      return;
    }
    if (selected !== null && targets.has(sq)) {
      tryMove(selected, sq, false);
      return;
    }
    if (selected === null && dropsTo.has(sq)) {
      props.onMove(moveToUci(dropsTo.get(sq)![0]), false);
      return;
    }
    if (movesFrom.has(sq)) {
      e.currentTarget.setPointerCapture(e.pointerId);
      drag.current = { sq, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, active: false, wasSelected: selected === sq };
      setSelected(sq);
      return;
    }
    setSelected(null);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    if (!d.active && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 5) d.active = true;
    if (d.active) {
      const rect = boardRef.current!.getBoundingClientRect();
      setDragPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    setDragPos(null);
    if (d.active) {
      const sq = squareAt(e.clientX, e.clientY);
      if (sq !== null && sq !== d.sq) tryMove(d.sq, sq, true);
    } else if (d.wasSelected) setSelected(null);
  };

  const onPointerCancel = () => {
    drag.current = null;
    setDragPos(null);
  };

  // ---- Animations from the last move's events ----
  const { anims, ghosts, effects } = useMemo(() => {
    const anims = new Map<number, Anim>();
    const ghosts: { sq: number; piece: number }[] = [];
    const effects: { sq: number; kind: string }[] = [];
    if (events) {
      let mainMoveSeen = false;
      for (const e of events) {
        if (e.t === 'move') {
          const neutral = isNeutral(e.piece);
          const main = !neutral && !mainMoveSeen;
          if (!neutral) mainMoveSeen = true;
          if (main && props.dragged) continue;
          anims.set(e.to, { from: e.from, slow: neutral });
        } else if (e.t === 'add' && isNeutral(e.piece)) {
          // A wall or lava took the square: it appears there rather than sliding in.
          anims.delete(e.sq);
        } else if (e.t === 'remove' && e.cause !== 'capture') ghosts.push({ sq: e.sq, piece: e.piece });
        else if (e.t === 'fx') effects.push({ sq: e.sq, kind: e.kind });
      }
    }
    return { anims, ghosts, effects };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, animKey]);

  const draggingSq = dragPos && drag.current?.active ? drag.current.sq : null;

  // ---- Squares ----
  const squares = [];
  for (let y = 0; y < 8; y++) {
    const row = [];
    for (let x = 0; x < 8; x++) {
      const sq = fromXY(x, y);
      const light = ((sq & 7) + (sq >> 3)) % 2 === 1;
      const classes = ['sq', light ? 'light' : 'dark'];
      if (sq === cursor) classes.push('cursor');
      if (props.lastMove && (sq === props.lastMove.from || sq === props.lastMove.to)) classes.push('last');
      if (sq === selected) classes.push('selected');
      if (sq === props.checkSquare) classes.push('in-check');
      else if (sq === props.dangerSquare) classes.push('in-danger');
      const isTarget = props.showLegalMoves && (targets.has(sq) || (selected === null && dropsTo.has(sq)));
      const occupied = position.board[sq] !== 0;
      row.push(
        <div key={sq} id={`${id}-${sq}`} role="gridcell" aria-label={squareLabel(sq)} aria-selected={sq === selected} className={classes.join(' ')}>
          {props.coordinates && x === 0 && <span className="coord rank" aria-hidden>{(sq >> 3) + 1}</span>}
          {props.coordinates && y === 7 && <span className="coord file" aria-hidden>{'abcdefgh'[sq & 7]}</span>}
          {isTarget && <span className={occupied ? 'hint capture' : 'hint'} />}
        </div>,
      );
    }
    squares.push(
      <div key={y} role="row" className="sq-row">
        {row}
      </div>,
    );
  }

  // ---- Decorations ----
  const marks = [];
  const tiles = [];
  const arrows = [];
  const badges = new Map<number, Extract<Decoration, { kind: 'badge' }>>();
  for (const [i, d] of props.decorations.entries()) {
    if (d.hint && !props.showHints) continue;
    if (d.kind === 'badge') {
      badges.set(d.sq, d);
      continue;
    }
    if (d.kind === 'mark') {
      const { x, y } = toXY(d.sq);
      marks.push(<div key={`m${i}`} className={`mark ${d.tone}`} style={{ left: pct(x), top: pct(y) }} />);
      continue;
    }
    if (d.kind === 'tile') {
      const { x, y } = toXY(d.sq);
      tiles.push(
        <div key={`t${i}`} className="tile" style={{ left: pct(x), top: pct(y) }} title={d.title}>
          <img src={d.image} alt="" draggable={false} />
        </div>,
      );
      continue;
    }
    const a = toXY(d.from);
    const b = toXY(d.to);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    arrows.push(
      <line
        key={`a${i}`}
        className={`arrow ${d.tone}`}
        x1={a.x + 0.5 + ux * 0.34}
        y1={a.y + 0.5 + uy * 0.34}
        x2={b.x + 0.5 - ux * 0.18}
        y2={b.y + 0.5 - uy * 0.18}
        markerEnd={`url(#head-${d.tone})`}
      />,
    );
  }

  // ---- Pieces ----
  const pieces = [];
  for (let sq = 0; sq < 64; sq++) {
    const p = position.board[sq];
    if (!p) continue;
    const { x, y } = toXY(sq);
    const style: CSSProperties & Record<string, string> = { left: pct(x), top: pct(y) };
    let key = `p${sq}`;
    let cls = 'piece';
    if (isNeutral(p)) cls += ' neutral';
    const anim = anims.get(sq);
    if (anim) {
      const from = toXY(anim.from);
      style['--dx'] = `${(from.x - x) * 100}%`;
      style['--dy'] = `${(from.y - y) * 100}%`;
      cls += anim.slow ? ' crawl' : ' slide';
      key += `-${animKey}`;
    }
    if (sq === draggingSq) cls += ' lifted';
    const badge = badges.get(sq);
    pieces.push(
      <div key={key} className={cls} style={style}>
        <img src={imageFor(ui, position, sq, p)} alt="" draggable={false} />
        {badge && <span className={`badge ${badge.tone}`}>{badge.text}</span>}
      </div>,
    );
  }
  for (const [i, g] of ghosts.entries()) {
    const { x, y } = toXY(g.sq);
    pieces.push(
      <div key={`ghost-${animKey}-${i}`} className="piece ghost" style={{ left: pct(x), top: pct(y) }}>
        <img src={imageFor(ui, position, g.sq, g.piece)} alt="" draggable={false} />
      </div>,
    );
  }
  for (const [i, f] of effects.entries()) {
    const { x, y } = toXY(f.sq);
    pieces.push(<div key={`fx-${animKey}-${i}`} className={`fx fx-${f.kind}`} style={{ left: pct(x), top: pct(y) }} />);
  }

  const dragging = draggingSq !== null && dragPos;
  const promoColor = promotion ? pieceColor(position.board[promotion.from]) : -1;

  return (
    <div
      className={`board ${legalMoves.length ? 'interactive' : ''} ${dropsTo.size ? 'placing' : ''} ${ui.boardClass ?? ''}`}
      ref={boardRef}
      role="grid"
      aria-label={`Chess board${legalMoves.length ? ': arrow keys to move around, Enter to pick up and drop a piece' : ''}`}
      aria-activedescendant={cursor !== null ? `${id}-${cursor}` : undefined}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onBlur={() => setCursor(null)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="squares">{squares}</div>
      {tiles}
      {marks}
      <svg className="arrows" viewBox="0 0 8 8" aria-hidden>
        <defs>
          {(['light', 'dark', 'danger'] as const).map((k) => (
            <marker key={k} id={`head-${k}`} className={`arrowhead ${k}`} viewBox="0 0 10 10" refX="5" refY="5" markerWidth="3.2" markerHeight="3.2" orient="auto-start-reverse">
              <path d="M0 0 L10 5 L0 10 Z" />
            </marker>
          ))}
        </defs>
        {arrows}
      </svg>
      <div className="pieces" aria-hidden>
        {pieces}
      </div>
      {cursor !== null && <div className="kb-cursor" aria-hidden style={{ left: pct(toXY(cursor).x), top: pct(toXY(cursor).y) }} />}
      {dragging && (
        <img className="drag-ghost" src={imageFor(ui, position, draggingSq, position.board[draggingSq])} alt="" style={{ left: dragPos.x, top: dragPos.y }} />
      )}
      {promotion && promoColor !== -1 && (
        <div className="promotion" onPointerDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <div className="promotion-card" role="dialog" aria-label="Promote to">
            <span>Promote to</span>
            <div className="promotion-options">
              {promotion.options.map((type, i) => (
                <button
                  key={type}
                  aria-label={pieceDef(type).name}
                  autoFocus={i === 0}
                  onClick={() => {
                    const m = (movesFrom.get(promotion.from) ?? []).find((mv) => moveTo(mv) === promotion.to && movePromo(mv) === type);
                    setPromotion(null);
                    setSelected(null);
                    if (m !== undefined) props.onMove(moveToUci(m), promotion.dragged);
                    boardRef.current?.focus();
                  }}
                >
                  <img src={pieceImage(makePiece(promoColor, type))} alt="" />
                </button>
              ))}
            </div>
            <button
              className="text-link"
              onClick={() => {
                setPromotion(null);
                boardRef.current?.focus();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
